/**
 * Thin, always-safe wrapper around the FullStory browser API.
 *
 * FullStory is not bundled with this app and is not loaded by any file in the repo:
 * resources/views/layouts/partials/fullstory.blade.php only calls FS('setIdentity', ...),
 * and the snippet that actually defines `window.FS` is injected by a GTM tag. That container
 * is deliberately withheld from staff — layouts/partials/google-tag-manager.blade.php skips
 * it entirely for Super Admin, Admin, Reviewer, Content Editor, Founding Team, Our Team,
 * Board of Directors, Advisory Board and Teacher Taskforce — and never loads outside
 * production. `window.FS` is therefore absent far more often than it is present, which is
 * why every call here has to no-op instead of throwing.
 *
 * The V2 API is in use (the blade partial's FS('setIdentity', {uid, properties}) is the V2
 * shape), so events go out as FS('trackEvent', {name, properties}). V2 infers property types,
 * so no `_str`/`_int` name suffixes.
 */

/** How long to hold events for a FullStory that has not landed yet. Mirrors the 5s the blade partial waits. */
const FLUSH_TIMEOUT = 5000;
const FLUSH_INTERVAL = 100;
/** Bound on the hold queue: if FS never loads (staff, ad blocker) these are simply dropped. */
const MAX_QUEUED = 20;

let queue = [];
let flushPoll = null;

/**
 * Drop any held events and stop waiting for FullStory.
 *
 * Tests only. The queue and its poll are module state, so without this a test that runs with
 * `window.FS` absent leaves events in flight that flush into the *next* test's spy.
 */
export function resetFullStoryQueue() {
    queue = [];
    if (flushPoll !== null && typeof window !== 'undefined') window.clearInterval(flushPoll);
    flushPoll = null;
}

/** True once the GTM-injected snippet has defined the FullStory API. */
export function fullStoryAvailable() {
    return typeof window !== 'undefined' && typeof window.FS === 'function';
}

function send(name, properties) {
    try {
        window.FS('trackEvent', { name, properties });
        return true;
    } catch (e) {
        // Instrumentation must never break the interaction it is measuring.
        return false;
    }
}

/**
 * Hold events until FS shows up.
 *
 * GTM is loaded async from the bottom of the body, so there is a short window after page load
 * where an interaction can happen before `window.FS` exists. Dropping those would reproduce
 * the very complaint this instrumentation exists to fix, so they are queued and flushed once.
 */
function startFlushPoll() {
    if (flushPoll || typeof window === 'undefined') return;

    let waited = 0;

    flushPoll = window.setInterval(() => {
        waited += FLUSH_INTERVAL;

        if (fullStoryAvailable()) {
            const held = queue;
            queue = [];
            window.clearInterval(flushPoll);
            flushPoll = null;
            held.forEach(event => send(event.name, event.properties));
            return;
        }

        if (waited >= FLUSH_TIMEOUT) {
            // FullStory is not coming (staff session, blocked, or non-production).
            queue = [];
            window.clearInterval(flushPoll);
            flushPoll = null;
        }
    }, FLUSH_INTERVAL);
}

/**
 * Record a custom FullStory event.
 *
 * @param {string} name       Human-readable event name — this is what funnel steps match on.
 * @param {Object} properties Non-sensitive metadata. Never put free text a visitor typed here.
 * @returns {boolean} true if it went straight out, false if queued, dropped, or FS is absent.
 */
export function trackFullStoryEvent(name, properties = {}) {
    if (typeof window === 'undefined') return false;

    if (fullStoryAvailable()) return send(name, properties);

    if (queue.length < MAX_QUEUED) {
        queue.push({ name, properties });
        startFlushPoll();
    }

    return false;
}
